import { useState } from "react";
import { creerEntreeDouane } from "../../oilmed.service";
import { showToast } from "../../utils";
import { useFournisseurs } from "../../useOilmed";

export default function EntreeDouaneForm({
  showForm,
  setShowForm,
  terminal,
  produit,
}) {
  const { liste: fournisseurs } = useFournisseurs(terminal);
  const [formData, setFormData] = useState({
    numeroPOT: "",
    fournisseur: "",
    volumeM3_initial: "",
    tonnageTM_initial: "",
    K: "",
    date: new Date().toISOString().split("T")[0], // Today's date
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      // Convert comma to point for numeric fields
      let processedValue = value;
      if (
        field === "volumeM3_initial" ||
        field === "tonnageTM_initial" ||
        field === "K"
      ) {
        processedValue = value.replace(/,/g, ".");
      }

      const updated = { ...prev, [field]: processedValue };

      // Auto-calculate K coefficient when both volume and tonnage are entered
      // K = Volume (M3) / Tonnage (TM) — conversion factor from TM to M3
      if (field === "volumeM3_initial" || field === "tonnageTM_initial") {
        const volume =
          field === "volumeM3_initial"
            ? Number(processedValue)
            : Number(prev.volumeM3_initial);
        const tonnage =
          field === "tonnageTM_initial"
            ? Number(processedValue)
            : Number(prev.tonnageTM_initial);

        if (volume > 0 && tonnage > 0) {
          updated.K = (volume / tonnage).toFixed(4);
        }
      }

      return updated;
    });
  };

  // Display helper: convert point to comma for display
  const displayValue = (value) => {
    if (!value) return "";
    return String(value).replace(/\./g, ",");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.numeroPOT.trim()) {
      showToast("Numéro POT est requis", "error");
      return;
    }
    if (!formData.fournisseur.trim()) {
      showToast("Fournisseur est requis", "error");
      return;
    }
    if (!formData.volumeM3_initial || Number(formData.volumeM3_initial) <= 0) {
      showToast("Volume M3 doit être supérieur à 0", "error");
      return;
    }
    if (
      !formData.tonnageTM_initial ||
      Number(formData.tonnageTM_initial) <= 0
    ) {
      showToast("Tonnage TM doit être supérieur à 0", "error");
      return;
    }
    if (!formData.K || Number(formData.K) <= 0) {
      showToast("Coefficient K doit être supérieur à 0", "error");
      return;
    }

    setSubmitting(true);
    try {
      await creerEntreeDouane({
        terminalId: terminal,
        produit,
        numeroPOT: formData.numeroPOT.trim(),
        fournisseur: formData.fournisseur.trim(),
        volumeM3_initial: Number(formData.volumeM3_initial),
        tonnageTM_initial: Number(formData.tonnageTM_initial),
        K: Number(formData.K),
        date: formData.date,
      });

      showToast("Lot créé avec succès ✔");
      setShowForm(false);
      setFormData({
        numeroPOT: "",
        fournisseur: "",
        volumeM3_initial: "",
        tonnageTM_initial: "",
        K: "",
        date: new Date().toISOString().split("T")[0],
      });
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (!showForm) return null;

  console.log("[EntreeDouaneForm] Rendering form, showForm:", showForm);

  return (
    <div className="modal-overlay open" onClick={() => setShowForm(false)}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-handle" />
        <div className="modal-title">Entrée Douane</div>
        <div className="modal-sub">
          {"// "}
          {terminal} · {produit.toUpperCase()} — NOUVEAU LOT
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: "0 20px 20px" }}>
            {/* Date */}
            <div className="form-group">
              <label className="form-label">Date</label>
              <input
                type="date"
                className="form-input"
                value={formData.date}
                onChange={(e) => handleChange("date", e.target.value)}
                required
              />
            </div>

            {/* Numéro POT */}
            <div className="form-group">
              <label className="form-label">Numéro POT *</label>
              <input
                type="text"
                className="form-input"
                value={formData.numeroPOT}
                onChange={(e) => handleChange("numeroPOT", e.target.value)}
                placeholder="KEM/SUPPLY/RDC/SEP/000448/25"
                required
              />
            </div>

            {/* Fournisseur */}
            <div className="form-group">
              <label className="form-label">Fournisseur *</label>
              {fournisseurs.length > 0 ? (
                <select
                  className="form-input"
                  value={formData.fournisseur}
                  onChange={(e) => handleChange("fournisseur", e.target.value)}
                  required
                >
                  <option value="">— Choisir —</option>
                  {fournisseurs.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  className="form-input"
                  value={formData.fournisseur}
                  onChange={(e) => handleChange("fournisseur", e.target.value)}
                  placeholder="KEMEXON"
                  required
                />
              )}
            </div>

            {/* Tonnage TM */}
            <div className="form-group">
              <label className="form-label">Tonnage TM Initial *</label>
              <input
                type="text"
                inputMode="decimal"
                className="form-input"
                value={displayValue(formData.tonnageTM_initial)}
                onChange={(e) =>
                  handleChange("tonnageTM_initial", e.target.value)
                }
                placeholder="150"
                required
              />
            </div>

            {/* Volume M3 */}
            <div className="form-group">
              <label className="form-label">Volume M3 Initial *</label>
              <input
                type="text"
                inputMode="decimal"
                className="form-input"
                value={displayValue(formData.volumeM3_initial)}
                onChange={(e) =>
                  handleChange("volumeM3_initial", e.target.value)
                }
                placeholder="199,49"
                required
              />
            </div>

            {/* K Coefficient — Auto-calculated */}
            <div className="form-group">
              <label className="form-label">
                Coefficient K {formData.K ? "(calculé automatiquement)" : "*"}
              </label>
              <input
                type="text"
                inputMode="decimal"
                className="form-input"
                value={displayValue(formData.K)}
                onChange={(e) => handleChange("K", e.target.value)}
                placeholder={
                  formData.volumeM3_initial && formData.tonnageTM_initial
                    ? "Auto-calculé"
                    : "1,3299"
                }
                readOnly={
                  formData.volumeM3_initial &&
                  formData.tonnageTM_initial &&
                  formData.K
                }
                style={
                  formData.K
                    ? { background: "var(--bg)", color: "var(--muted)" }
                    : {}
                }
              />
              {formData.K && (
                <div
                  style={{ fontSize: 10, color: "var(--muted)", marginTop: 4 }}
                >
                  ℹ️ K = {displayValue(formData.volumeM3_initial)} m³ ÷{" "}
                  {displayValue(formData.tonnageTM_initial)} TM ={" "}
                  {displayValue(formData.K)}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="btn-row">
              <button
                type="submit"
                className="btn btn-blue"
                style={{ flex: 1 }}
                disabled={submitting}
              >
                {submitting ? "Création..." : "✔ Créer le lot"}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowForm(false)}
              >
                Annuler
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
